import { useQueryClient } from '@tanstack/react-query'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useSheet } from './SheetContext'

export interface Rule {
  type: string
  sourceField?: string
  destinationFields?: string[]
  summary?: string
  toolId?: string
  name?: string
  disabled?: boolean
  config: {
    changes: any
    name: string
    type: string
    sourceFields?: string[]
    sourceField?: string
    destinationFields?: string[]
    destinationField?: string
    summary?: string
    fieldType?: string
    function?: string
    description?: string
    position?: 'left' | 'right'
  }
}

interface RulesContextType {
  pendingRules: Rule[]
  addPendingRules: (newRules: Rule[]) => void
  setPendingRules: (newRules: Rule[]) => void
  clearPendingRules: () => void
  removeRule: (toolId: string) => void
}

const RulesContext = createContext<RulesContextType | undefined>(undefined)

export const RulesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const queryClient = useQueryClient()
  const [pendingRules, setPendingRules] = useState<Rule[]>([])

  const addPendingRules = useCallback((newRules: Rule[]) => {
    setPendingRules((prevRules) => [...prevRules, ...newRules])
  }, [])

  useEffect(() => {
    window.postMessage({ type: 'refetchRecords' })
  }, [pendingRules])

  const clearPendingRules = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: ['sheets'],
      refetchType: 'active',
    })
    setTimeout(() => {
      setPendingRules([])
    }, 1000)
  }, [])

  const removeRule = useCallback((toolId: string) => {
    setPendingRules((prevRules) =>
      prevRules.filter((rule) => rule.toolId !== toolId)
    )
  }, [])

  const value = useMemo(
    () => ({
      pendingRules,
      addPendingRules,
      setPendingRules,
      clearPendingRules,
      removeRule,
    }),
    [pendingRules, addPendingRules, clearPendingRules, removeRule]
  )

  return <RulesContext.Provider value={value}>{children}</RulesContext.Provider>
}

export const useRulesContext = () => {
  const context = useContext(RulesContext)
  if (context === undefined) {
    throw new Error('useRules must be used within a RulesProvider')
  }
  return context
}

// Utility function for components to get only enabled rules
export function getPendingRules(sheet: { rules?: any[] }) {
  return (sheet?.rules ?? []).filter((rule: any) => rule.disabled !== true)
}
